import { Button } from "@admin/components/atoms/Button";
import { DataTable, DataTableColumn } from "@admin/components/molecules/Table";
import { getSuppliersByCompanyId, Supplier } from "@enos-monorepo/lib";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { UpsertSupplierModal } from "../Modals/UpsertSupplierModal";
import { EditSupplierStatusModal } from "./Modals/EditSupplierStatusModal";

export enum SupplierModalAction {
  ADD = "Add",
  EDIT = "Edit",
}

interface SuppliersTableProps {
  companyId?: string;
}

export function SuppliersTable({ companyId }: SuppliersTableProps) {
  const { t } = useTranslation();
  const { data: suppliers, isPending } = useQuery({
    queryKey: ["suppliersByCompany", companyId],
    queryFn: () => getSuppliersByCompanyId(companyId ?? ""),
    enabled: !!companyId,
  });
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier>();
  const [isAddSupplierModalOpen, setIsAddSupplierModalOpen] = useState(false);
  const [isEditSupplierModalOpen, setIsEditSupplierModalOpen] = useState(false);
  const [isEditSupplierStatusModalOpen, setIsEditSupplierStatusModalOpen] =
    useState(false);
  const [showInactiveSuppliers, setShowInactiveSuppliers] = useState(false);

  const dataColumns: DataTableColumn<Supplier>[] = [
    {
      header: t("common.Name"),
      accessorKey: "name",
      size: 270,
      cell: ({ row }) => row.original.name,
    },
    {
      header: t("common.Email"),
      accessorKey: "email",
      size: 270,
      cell: ({ row }) => row.original.email,
    },
    {
      header: t("common.Phone Number"),
      accessorKey: "phone",
      size: 270,
      cell: ({ row }) => row.original.phone,
    },
    {
      header: t("common.Address"),
      accessorKey: "addresse",
      size: 270,
      cell: ({ row }) => row.original.adresse,
    },
    {
      align: "center",
      header: t("common.Actions"),
      size: 110,
      cell: ({ row }) => (
        <div className="flex justify-center">
          <Button
            variant="clear"
            icon
            size="sm"
            onClick={() => setIsEditSupplierModalOpen(true)}
          >
            <i className="ki-filled ki-notepad-edit"></i>
          </Button>

          <Button
            onClick={() => setIsEditSupplierStatusModalOpen(true)}
            icon
            property={showInactiveSuppliers ? "success" : "danger"}
            variant="clear"
            size="sm"
          >
            {showInactiveSuppliers ? (
              <i className="ki-solid ki-left-square"></i>
            ) : (
              <i className="ki-filled ki-trash-square"></i>
            )}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <DataTable
        loading={isPending}
        onRowClick={setSelectedSupplier}
        columns={dataColumns}
        data={
          showInactiveSuppliers
            ? suppliers?.filter((sp) => !!sp.isDeleted)
            : suppliers?.filter((sp) => !sp.isDeleted)
        }
        title={t("food.Suppliers")}
        header={
          <div className="flex flex-row gap-[30px]">
            <div className="flex items-center gap-2">
              <label className="switch switch-md">
                <input
                  checked={showInactiveSuppliers}
                  onChange={(e) => setShowInactiveSuppliers((prev) => !prev)}
                  name="check"
                  type="checkbox"
                />
                <span className="switch-label">
                  {!showInactiveSuppliers
                    ? "Show Inactives"
                    : "Showing Inactives"}
                </span>
              </label>
            </div>
            <Button onClick={() => setIsAddSupplierModalOpen(true)} size="sm">
              <i className="ki-filled ki-plus-squared"></i>
              {t("food.Add New Supplier")}
            </Button>
          </div>
        }
        isInfoIcon={false}
      />
      <UpsertSupplierModal
        open={isAddSupplierModalOpen}
        onClose={() => setIsAddSupplierModalOpen(false)}
        companyId={companyId}
        type={SupplierModalAction.ADD}
      />
      <UpsertSupplierModal
        open={isEditSupplierModalOpen}
        onClose={() => setIsEditSupplierModalOpen(false)}
        companyId={companyId}
        supplier={selectedSupplier}
        type={SupplierModalAction.EDIT}
      />
      <EditSupplierStatusModal
        supplier={selectedSupplier}
        open={isEditSupplierStatusModalOpen}
        onClose={() => setIsEditSupplierStatusModalOpen(false)}
      />
    </>
  );
}
